import React from 'react';
import { motion } from 'framer-motion';
import SectionHeader from '../SectionHeader/SectionHeader';

const techCategories = [
  {
    title: 'Frontend',
    color: '#EFCD86',
    technologies: [
      { name: 'React', iconSrc: '/assest/icon/react.png' },
      { name: 'Next.js', iconSrc: '/assest/icon/nextjs.png' },
      { name: 'Vue.js', iconSrc: '/assest/icon/Vuejs.png' },
      { name: 'TypeScript', iconSrc: '/assest/icon/TypeScript.png'  },
      { name: 'Tailwind', iconSrc: '/assest/icon/Tailwind.png'  },
      { name: 'Flutter', iconSrc: '/assest/icon/Flutter.png'  },
    ],
  },
  {
    title: 'Backend',
    color: '#046A8C',
    technologies: [
      { name: 'Node.js', iconSrc: '/assest/icon/Nodejs.png' },
      { name: 'Python', iconSrc: '/assest/icon/Python.png' },
      { name: 'Go', iconSrc: '/assest/icon/Go.png' },
      { name: 'Java', iconSrc: '/assest/icon/Java.png' },
      { name: 'C#', iconSrc: '/assest/icon/Csharp.png' },
      { name: 'Rust', iconSrc: '/assest/icon/Rust.png' },
    ],
  },
  {
    title: 'Database',
    color: '#66B152',
    technologies: [
      { name: 'PostgreSQL', iconSrc: '/assest/icon/PostgreSQL.png' },
      { name: 'MongoDB', iconSrc: '/assest/icon/MongoDB.png' },
      { name: 'Redis', iconSrc: '/assest/icon/Redis.png' },
      { name: 'MySQL', iconSrc: '/assest/icon/MySQL.png' },
      { name: 'Firebase', iconSrc: '/assest/icon/Firebase.png' },
      { name: 'Supabase', iconSrc: '/assest/icon/Supabase.png' },
    ],
  },
  {
    title: 'Cloud & DevOps',
    color: '#EFCD86',
    technologies: [
      { name: 'AWS', iconSrc: '/assest/icon/AWS.png' },
      { name: 'Azure', iconSrc: '/assest/icon/Azure.png' },
      { name: 'GCP', iconSrc: '/assest/icon/GCP.png' },
      { name: 'Docker', iconSrc: '/assest/icon/Docker.png' },
      { name: 'Kubernetes', iconSrc: '/assest/icon/Kubernetes.png' },
      { name: 'CI/CD', iconSrc: '/assest/icon/CICD.png' },
    ],
  },
  {
    title: 'AI / ML / Data',
    color: '#66B152',
    technologies: [
      { name: 'OpenAI', iconSrc: '/assest/icon/Openai.png' },
      { name: 'TensorFlow', iconSrc: '/assest/icon/TensorFlow.png' },
      { name: 'PyTorch', iconSrc: '/assest/icon/PyTorch.png' },
      { name: 'LangChain', iconSrc: '/assest/icon/LangChain.png' },
      { name: 'Pinecone', iconSrc: '/assest/icon/Pinecone.png' },
      { name: 'Hugging Face', iconSrc: '/assest/icon/HuggingFace.png' },
    ],
  },
  {
    title: 'Mobile',
    color: '#046A8C',
    technologies: [
      { name: 'React Native', iconSrc: '/assest/icon/ReactNative.png' },
      { name: 'Flutter', iconSrc: '/assest/icon/Flutter.png' },
      { name: 'Swift', iconSrc: '/assest/icon/Swift.png' },
      { name: 'Kotlin', iconSrc: '/assest/icon/Kotlin.png' },
      { name: 'iOS', iconSrc: '/assest/icon/iOS.png' },
      { name: 'Android', iconSrc: '/assest/icon/Android.png' },
    ],
  },
];

const TechStack = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0e3a63]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader 
          badge="Services"
          title="Our Tech Stack"
          subtitle="We leverage cutting-edge technologies including AI/ML to build scalable, future-proof solutions for your business."
          accentColor="green"
        />

        {/* Tech Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {techCategories.map((category, index) => (
            <motion.div
              key={category.title}
              variants={itemVariants}
              className="group relative rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02]"
              style={{
                background: 'rgba(13, 31, 51, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
              }}
            >
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-1 h-8 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
                <h3 className="text-xl font-bold text-white group-hover:text-[#EFCD86] transition-colors">
                  {category.title}
                </h3>
              </div>

              {/* Technologies Grid */}
              <div className="grid grid-cols-3 gap-3">
                {category.technologies.map((tech) => (
                  <div
                    key={tech.name}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-300 hover:bg-white/5 group/tech"
                  >
                    <img
                      src={tech.iconSrc}
                      alt={`${tech.name} logo`}
                      className="w-8 h-8 object-contain filter grayscale group-hover/tech:grayscale-0 transition-all"
                      loading="lazy"
                    />
                    <span className="text-xs text-white/90 text-center group-hover/tech:text-white transition-colors">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>

              {/* Hover Glow Effect */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at 50% 0%, ${category.color}10, transparent 70%)`,
                }}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-white/90 mb-4">
            Not sure which technology fits your project?
          </p>
          <button className="px-8 py-3 rounded-lg bg-[#046A8C] text-white font-semibold hover:bg-[#0588B3] transition-all duration-300">
            Get Free Tech Consultation
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default TechStack;